import { memo } from "react";
import { Check, X, Search } from "lucide-react";
import { useState, useMemo } from "react";

export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇮🇳", native: "English" },
  { code: "hi", name: "Hindi", flag: "🇮🇳", native: "हिंदी" },
  { code: "bn", name: "Bengali", flag: "🇮🇳", native: "বাংলা" },
  { code: "ta", name: "Tamil", flag: "🇮🇳", native: "தமிழ்" },
  { code: "te", name: "Telugu", flag: "🇮🇳", native: "తెలుగు" },
  { code: "mr", name: "Marathi", flag: "🇮🇳", native: "मराठी" },
  { code: "gu", name: "Gujarati", flag: "🇮🇳", native: "ગુજરાતી" },
  { code: "kn", name: "Kannada", flag: "🇮🇳", native: "ಕನ್ನಡ" },
  { code: "ml", name: "Malayalam", flag: "🇮🇳", native: "മലയാളം" },
  { code: "pa", name: "Punjabi", flag: "🇮🇳", native: "ਪੰਜਾਬੀ" },
  { code: "or", name: "Odia", flag: "🇮🇳", native: "ଓଡ଼ିଆ" },
  { code: "ur", name: "Urdu", flag: "🇮🇳", native: "اردو" },
  { code: "as", name: "Assamese", flag: "🇮🇳", native: "অসমীয়া" },
  { code: "ne", name: "Nepali", flag: "🇳🇵", native: "नेपाली" }
];

// Popular languages shown first
const POPULAR_CODES = ["en", "hi", "ta", "te", "bn", "mr"];

export const LanguageSelector = memo(({ 
  selectedLanguage, 
  setSelectedLanguage, 
  respondInSameLanguage, 
  setRespondInSameLanguage,
  onClose
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  
  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) return LANGUAGES;
    const query = searchQuery.toLowerCase();
    return LANGUAGES.filter(lang => 
      lang.name.toLowerCase().includes(query) ||
      lang.native.toLowerCase().includes(query) ||
      lang.code.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const popularLanguages = LANGUAGES.filter(l => POPULAR_CODES.includes(l.code));
  const otherLanguages = filteredLanguages.filter(l => !POPULAR_CODES.includes(l.code));

  const handleSelect = (code) => {
    setSelectedLanguage(code);
    // Auto-enable respond in same language for non-English
    if (code !== 'en') {
      setRespondInSameLanguage(true);
    }
    onClose?.();
  };

  return (
    <div className="bg-emerald-50 border-b border-emerald-100 animate-in slide-in-from-top-2 duration-200">
      {/* Search Bar */}
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
          <input
            type="text"
            placeholder="Search languages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent placeholder:text-emerald-400"
            data-testid="language-search"
          />
        </div>
      </div>

      {/* Popular Languages */}
      {!searchQuery && (
        <div className="px-4 pb-2">
          <p className="text-[10px] uppercase tracking-wider text-emerald-500 font-semibold mb-2">Popular</p>
          <div className="flex flex-wrap gap-2">
            {popularLanguages.map((lang) => (
              <LanguageChip 
                key={lang.code}
                lang={lang}
                isSelected={selectedLanguage === lang.code}
                onSelect={() => handleSelect(lang.code)}
              />
            ))}
          </div>
        </div>
      )}

      {/* All/Filtered Languages */}
      <div className="px-4 pb-3">
        {!searchQuery && <p className="text-[10px] uppercase tracking-wider text-emerald-500 font-semibold mb-2">All Languages</p>}
        <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto scrollbar-thin scrollbar-thumb-emerald-200 scrollbar-track-transparent">
          {(searchQuery ? filteredLanguages : otherLanguages).map((lang) => (
            <LanguageChip 
              key={lang.code}
              lang={lang}
              isSelected={selectedLanguage === lang.code}
              onSelect={() => handleSelect(lang.code)}
              showNative
            />
          ))}
          {searchQuery && filteredLanguages.length === 0 && (
            <p className="text-sm text-emerald-500 italic py-2">No languages found</p>
          )}
        </div>
      </div>

      {/* Auto-respond toggle */}
      <div className="px-4 pb-3 flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer group">
          <div className={`relative w-10 h-5 rounded-full transition-colors ${respondInSameLanguage ? 'bg-lime-500' : 'bg-emerald-200'}`}>
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${respondInSameLanguage ? 'translate-x-5' : 'translate-x-0.5'}`}>
              {respondInSameLanguage && <Check className="w-3 h-3 text-lime-500 m-0.5" />}
            </div>
          </div>
          <span className="text-xs text-emerald-700 group-hover:text-emerald-900 transition-colors">
            Auto-respond in {LANGUAGES.find(l => l.code === selectedLanguage)?.name || 'selected language'}
          </span>
        </label>
        <button 
          onClick={onClose}
          className="text-emerald-500 hover:text-emerald-700 p-1 rounded-full hover:bg-emerald-100 transition-all"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
});

// Language chip component
const LanguageChip = memo(({ lang, isSelected, onSelect, showNative }) => (
  <button
    onClick={onSelect}
    className={`
      flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium 
      transition-all duration-200 transform hover:scale-105
      ${isSelected
        ? 'bg-lime-500 text-white shadow-md shadow-lime-500/30 ring-2 ring-lime-300'
        : 'bg-white text-emerald-700 hover:bg-lime-50 border border-emerald-200 hover:border-lime-300'
      }
    `}
    data-testid={`lang-${lang.code}`}
  >
    <span className="text-sm">{lang.flag}</span>
    <span>{lang.name}</span>
    {showNative && lang.code !== 'en' && (
      <span className="opacity-70">({lang.native})</span>
    )}
    {isSelected && <Check className="w-3 h-3 ml-0.5" />}
  </button>
));
